import rt from "aldoo-ra/Realtime";
import PubSub from "pubsub-js";

/**
 * Realtime Task Manager Controller
 * Socket.io based real-time communication for task operations
 *
 * Events published via PubSub:
 * - 'task:changed' - Task changed (includes taskId)
 * - 'task:connection_change' - Connection status changed
 */
function createRealtimeTaskController() {
    let taskSocket = null;
    let isInitialized = false;
    let reconnectTimer = null;
    let isReconnecting = false;
    let connectionStatus = 'disconnected'; // 'disconnected' | 'connecting' | 'connected'

    // Notify connection state change
    const notifyConnectionChange = (status) => {
        connectionStatus = status;
        PubSub.publish('task:connection_change', status);
    };

    // Attempt to reconnect when needed
    const attemptReconnection = async () => {
        if (isReconnecting || reconnectTimer) return;

        if (taskSocket?.connected) return;

        isReconnecting = true;
        notifyConnectionChange('connecting');

        try {
            if (taskSocket && !taskSocket.connected) {
                taskSocket.removeAllListeners();
                taskSocket.disconnect();
                taskSocket = null;
            }
            isInitialized = false;

            await new Promise(resolve => setTimeout(resolve, 1000));

            const newSocket = initialize();
            if (!newSocket) {
                throw new Error('Failed to initialize new socket connection');
            }

            await subscribeToAllTasks();

            isReconnecting = false;
            notifyConnectionChange('connected');
        } catch (error) {
            console.error('❌ Task Manager: Reconnection failed:', error.message);
            isReconnecting = false;
            notifyConnectionChange('disconnected');

            reconnectTimer = setTimeout(() => {
                reconnectTimer = null;
                attemptReconnection();
            }, 5000);
        }
    };

    // Initialize socket connection
    const initialize = () => {
        if (isInitialized) return taskSocket;

        try {
            notifyConnectionChange('connecting');
            taskSocket = rt('task/manager');
            isInitialized = true;

            // Handle socket errors
            taskSocket.on('error', (error) => {
                console.error('Task socket error:', error);
                notifyConnectionChange('disconnected');
            });

            taskSocket.on('disconnect', () => {
                console.warn('Task socket disconnected');
                notifyConnectionChange('disconnected');
                attemptReconnection();
            });

            taskSocket.on('connect', () => {
                notifyConnectionChange('connected');
            });

            // Simple event listener for task changes
            taskSocket.on('task:changed', (data) => {
                PubSub.publish('task:changed', data.taskId);
            });

            return taskSocket;
        } catch (error) {
            console.error('Realtime Task Controller: Failed to connect:', error);
            notifyConnectionChange('disconnected');
            throw error;
        }
    };

    // Subscribe to all tasks
    const subscribeToAllTasks = async () => {
        if (!taskSocket) {
            throw new Error('Controller not initialized. Call initialize() first.');
        }

        return new Promise((resolve, reject) => {
            taskSocket.emit('task:subscribe_all', {}, (response) => {
                if (response.success && response.data?.subscribed) {
                    resolve(response.data);
                } else {
                    reject(new Error(response.error || response.data?.error || 'Failed to subscribe to all tasks'));
                }
            });
        });
    };

    // Subscribe to specific task
    const subscribeToTask = async (taskId) => {
        if (!taskSocket) {
            throw new Error('Controller not initialized. Call initialize() first.');
        }

        return new Promise((resolve, reject) => {
            taskSocket.emit('task:subscribe', { id: taskId }, (response) => {
                if (response.subscribed) {
                    resolve(response);
                } else {
                    reject(new Error(response.error || 'Failed to subscribe to task'));
                }
            });
        });
    };

    // Unsubscribe from specific task
    const unsubscribeFromTask = async (taskId) => {
        if (!taskSocket) {
            throw new Error('Controller not initialized. Call initialize() first.');
        }

        return new Promise((resolve, reject) => {
            taskSocket.emit('task:unsubscribe', { id: taskId }, (response) => {
                if (response.unsubscribed) {
                    resolve(response);
                } else {
                    reject(new Error(response.error || 'Failed to unsubscribe from task'));
                }
            });
        });
    };

    // Get task list
    const getTaskList = async (options = {}) => {
        if (!taskSocket) {
            throw new Error('Controller not initialized. Call initialize() first.');
        }

        return new Promise((resolve, reject) => {
            taskSocket.emit('task:list', options, (response) => {
                if (response.success && response.data) {
                    resolve(response.data);
                } else {
                    reject(new Error(response.error || response.data?.error || 'Failed to get task list'));
                }
            });
        });
    };

    // Get specific task
    const getTask = async (taskId) => {
        if (!taskSocket) {
            throw new Error('Controller not initialized. Call initialize() first.');
        }

        return new Promise((resolve, reject) => {
            taskSocket.emit('task:get', { id: taskId }, (response) => {
                if (response.success && response.data?.task) {
                    resolve(response.data.task);
                } else if (response.task) {
                    // Legacy format support
                    resolve(response.task);
                } else {
                    reject(new Error(response.error || response.data?.error || 'Failed to get task'));
                }
            });
        });
    };

    // Stop task
    const stopTask = async (taskId) => {
        if (!taskSocket) {
            throw new Error('Controller not initialized. Call initialize() first.');
        }

        return new Promise((resolve, reject) => {
            taskSocket.emit('task:stop', { id: taskId }, (response) => {
                if (response?.error) {
                    reject(new Error(response.error));
                } else if (response?.success && response?.data?.stopped) {
                    resolve(response.data);
                } else if (response?.stopped) {
                    // Legacy format support
                    resolve(response);
                } else {
                    reject(new Error(`Failed to stop task: Invalid response`));
                }
            });
        });
    };

    // Get available scripts
    const getScripts = async () => {
        if (!taskSocket) {
            throw new Error('Controller not initialized. Call initialize() first.');
        }

        return new Promise((resolve, reject) => {
            taskSocket.emit('task:scripts', {}, (response) => {
                if (response?.error) {
                    reject(new Error(response.error));
                } else if (response?.success && response?.data?.scripts) {
                    resolve(response.data.scripts);
                } else if (response?.scripts) {
                    // Legacy format support
                    resolve(response.scripts);
                } else {
                    console.error('[getScripts] Invalid response format:', response);
                    reject(new Error('Failed to load scripts: Invalid response'));
                }
            });
        });
    };

    // Get script source code
    const getScriptSource = async (scriptName) => {
        if (!taskSocket) {
            throw new Error('Controller not initialized. Call initialize() first.');
        }

        return new Promise((resolve, reject) => {
            taskSocket.emit('task:script-source', { scriptName }, (response) => {
                if (response?.success) {
                    resolve(response);
                } else {
                    reject(new Error(response?.error || 'Failed to load script source'));
                }
            });
        });
    };

    // Run script
    const runScript = async (scriptName, env = {}) => {
        if (!taskSocket) {
            throw new Error('Controller not initialized. Call initialize() first.');
        }

        return new Promise((resolve, reject) => {
            taskSocket.emit('task:run', { script: scriptName, env }, (response) => {
                if (response?.error) {
                    const error = new Error(response.error);
                    error.code = response.errorCode;
                    reject(error);
                } else if (response?.success) {
                    resolve(response);
                } else {
                    reject(new Error('Failed to run script: Invalid response'));
                }
            });
        });
    };

    // Get global environment
    const getGlobalEnv = async () => {
        if (!taskSocket) {
            throw new Error('Controller not initialized. Call initialize() first.');
        }

        return new Promise((resolve, reject) => {
            taskSocket.emit('task:global_env', {}, (response) => {
                if (response?.error) {
                    reject(new Error(response.error));
                } else if (response?.success && response?.data?.entries) {
                    resolve(response.data.entries);
                } else if (response?.entries) {
                    resolve(response.entries);
                } else {
                    reject(new Error('Failed to load global environment: Invalid response'));
                }
            });
        });
    };


    // Cleanup
    const cleanup = () => {
        if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }

        if (taskSocket) {
            taskSocket.removeAllListeners();
            taskSocket.disconnect();
            taskSocket = null;
        }

        isInitialized = false;
    };

    return {
        initialize,
        subscribeToAllTasks,
        subscribeToTask,
        unsubscribeFromTask,
        getTaskList,
        getTask,
        stopTask,
        getScripts,
        getScriptSource,
        runScript,
        getGlobalEnv,
        cleanup,
        get isConnected() {
            return taskSocket?.connected || false;
        },
        get connectionStatus() {
            return connectionStatus;
        }
    };
}

// Singleton instance
let controllerInstance = null;

export default function getTaskController() {
    if (!controllerInstance) {
        controllerInstance = createRealtimeTaskController();
    }
    return controllerInstance;
}
